import type { Observable } from './Observable';
import type { UndoRedoCommandInterface } from './UndoRedoCommand';

export type UndoRedoStateChangeListener = () => void;

export class UndoRedoManager implements Observable {
  private static undoRedoManagerInstance: UndoRedoManager;
  private readonly undoStack: UndoRedoCommandInterface[];
  private readonly stackLimit: number;
  private redoStack: UndoRedoCommandInterface[];
  private isRunningACommand: boolean;
  private listeners: Set<UndoRedoStateChangeListener>;

  private constructor() {
    this.undoStack = [];
    this.redoStack = [];
    this.stackLimit = 20;
    this.isRunningACommand = false;
    this.listeners = new Set();
  }

  static getUndoRedoManager(): UndoRedoManager {
    if (!UndoRedoManager.undoRedoManagerInstance) {
      UndoRedoManager.undoRedoManagerInstance = new UndoRedoManager();
    }
    return UndoRedoManager.undoRedoManagerInstance;
  }

  subscribe(listener: UndoRedoStateChangeListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyStateChange(): void {
    this.listeners.forEach((listener) => listener());
  }

  private resetRedoStack(): void {
    this.redoStack = [];
    this.notifyStateChange();
  }

  pushUndoRedoCommands(command: UndoRedoCommandInterface): void {
    if (this.isRunningACommand) return;

    if (this.undoStack.length === this.stackLimit) {
      this.undoStack.shift();
    }
    this.undoStack.push(command);
    this.resetRedoStack();
    this.notifyStateChange();
  }

  isUndoStackEmpty(): boolean {
    return this.undoStack.length === 0;
  }

  isRedoStackEmpty(): boolean {
    return this.redoStack.length === 0;
  }

  async runUndoCommand(): Promise<void> {
    const commandToBeExecuted = this.undoStack.pop();
    if (!commandToBeExecuted) {
      this.notifyStateChange();
      return;
    }

    this.isRunningACommand = true;
    const wasCommandSuccessful = await commandToBeExecuted.undo();
    this.isRunningACommand = false;
    if (!wasCommandSuccessful) {
      await this.runUndoCommand();
      return;
    }

    this.redoStack.push(commandToBeExecuted);
    this.notifyStateChange();
  }

  async runRedoCommand(): Promise<void> {
    const commandToBeExecuted = this.redoStack.pop();
    if (!commandToBeExecuted) {
      this.notifyStateChange();
      return;
    }

    this.isRunningACommand = true;
    const wasCommandSuccessful = await commandToBeExecuted.redo();
    this.isRunningACommand = false;
    if (!wasCommandSuccessful) {
      await this.runRedoCommand();
      return;
    }

    this.undoStack.push(commandToBeExecuted);
    this.notifyStateChange();
  }
}
